export const buttonClassNames = {
  base: [
    "inline-flex min-w-0 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-md)] border border-transparent text-sm font-medium leading-none outline-none transition-[background-color,border-color,color,box-shadow,opacity,transform] duration-150",
    "disabled:pointer-events-none disabled:opacity-50",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-ring)]",
    "aria-invalid:border-[color:var(--color-destructive)] aria-invalid:ring-2 aria-invalid:ring-[color:var(--color-destructive)]",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:size-4",
  ].join(" "),
  variants: {
    default: "theme-button-primary enabled:hover:-translate-y-px",
    destructive:
      "bg-[color:var(--color-destructive)] text-[color:var(--color-destructive-foreground)] enabled:hover:opacity-95 focus-visible:ring-[color:var(--color-destructive)]",
    outline: "theme-button-tertiary enabled:hover:-translate-y-px",
    secondary: "theme-button-secondary enabled:hover:-translate-y-px",
    ghost: "theme-button-ghost text-inherit",
    link: "text-[color:var(--color-primary)] underline underline-offset-4 enabled:hover:opacity-85",
  },
  sizes: {
    default: "min-h-9 px-4 py-2",
    sm: "min-h-8 gap-1.5 px-3 py-2",
    lg: "min-h-10 px-6 py-2",
    icon: "size-9 p-0",
    "icon-sm": "size-8 p-0",
    "icon-lg": "size-10 p-0",
  },
} as const

export const badgeClassNames = {
  base: [
    "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-[var(--radius-pill)] border border-transparent px-2 py-0.5 text-xs font-medium leading-5 text-[color:var(--color-foreground)] outline-none transition-colors duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-ring)]",
    "aria-invalid:border-[color:var(--color-destructive)] aria-invalid:ring-2 aria-invalid:ring-[color:var(--color-destructive)]",
    "[&_svg]:pointer-events-none [&_svg]:size-3 [&_svg]:shrink-0",
  ].join(" "),
  variants: {
    default:
      "bg-[color:var(--color-primary)] text-[color:var(--color-primary-foreground)] hover:opacity-95",
    secondary:
      "border-[color:var(--button-secondary-border)] bg-[color:var(--session-accent-soft)] text-[color:var(--session-accent-ink)] hover:bg-[color:var(--session-accent-soft-strong)]",
    destructive:
      "bg-[color:var(--color-destructive)] text-[color:var(--color-destructive-foreground)] hover:opacity-95 focus-visible:ring-[color:var(--color-destructive)]",
    outline:
      "border-[color:var(--button-tertiary-border)] bg-transparent text-[color:var(--button-tertiary-text)] hover:bg-[color:var(--button-ghost-hover)]",
  },
} as const

export const cardClassNames = {
  card: "theme-surface-panel flex flex-col gap-6 rounded-[var(--radius-lg)] border py-6 text-[color:var(--color-card-foreground)]",
  header:
    "grid auto-rows-min items-start gap-2 px-6 [&:has([data-slot=card-action])]:grid-cols-[minmax(0,1fr)_auto]",
  title: "font-semibold leading-none",
  description: "text-sm text-[color:var(--color-muted-foreground)]",
  action: "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
  content: "px-6",
  footer: "flex items-center px-6",
} as const

export const inputClassName = [
  "flex h-9 w-full min-w-0 rounded-[var(--radius-md)] border border-[color:var(--color-input)] bg-transparent px-3 py-1 text-base text-inherit shadow-[var(--shadow-xs)] outline-none transition-[border-color,box-shadow,color] duration-150",
  "placeholder:text-[color:var(--color-muted-foreground)]",
  "selection:bg-[color:var(--color-primary)] selection:text-[color:var(--color-primary-foreground)]",
  "file:mr-3 file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-inherit",
  "focus-visible:border-[color:var(--color-ring)] focus-visible:ring-2 focus-visible:ring-[color:var(--color-ring)]",
  "aria-invalid:border-[color:var(--color-destructive)] aria-invalid:ring-2 aria-invalid:ring-[color:var(--color-destructive)]",
  "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
].join(" ")

export const labelClassName = [
  "inline-flex items-center gap-2 text-sm font-medium leading-none text-[color:var(--color-foreground)] select-none",
  "peer-disabled:cursor-not-allowed peer-disabled:opacity-50",
  "group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50",
].join(" ")

export const separatorClassName =
  "shrink-0 bg-[color:var(--color-border)] data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px"

export const scrollAreaClassName = [
  "relative overflow-auto rounded-[inherit] outline-none transition-[color,box-shadow] duration-150",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-ring)]",
].join(" ")

export const tabsClassNames = {
  root: "flex flex-col gap-2",
  list: "inline-flex w-fit items-center justify-center rounded-[var(--radius-lg)] bg-[color:var(--session-accent-soft)] p-[3px] text-[color:var(--color-muted-foreground)]",
  trigger: [
    "inline-flex min-h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-[var(--radius-sm)] border border-transparent bg-transparent px-2.5 py-[0.45rem] text-sm font-medium leading-none text-[color:var(--color-muted-foreground)] outline-none transition-colors duration-150",
    "hover:text-[color:var(--color-foreground)]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-ring)]",
    "disabled:pointer-events-none disabled:opacity-50",
    "data-[state=active]:border-[color:var(--color-border)] data-[state=active]:bg-[color:var(--color-card)] data-[state=active]:text-[color:var(--color-foreground)] data-[state=active]:shadow-[var(--shadow-xs)]",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  content: "flex-1 outline-none",
} as const

export const tableClassNames = {
  container: "relative w-full overflow-x-auto",
  table: "w-full border-separate border-spacing-0 caption-bottom text-sm",
  header: "[&_tr]:border-b [&_tr]:border-[color:var(--color-border)]",
  body: "[&_tr:last-child]:border-0",
  footer:
    "border-t border-[color:var(--color-border)] bg-[color:var(--session-accent-soft)] font-medium [&>tr:last-child]:border-0",
  row: "border-b border-[color:var(--color-border)] transition-colors hover:bg-[color:var(--button-ghost-hover)] data-[state=selected]:bg-[color:var(--color-muted)]",
  head: "h-10 px-2 text-left align-middle font-medium whitespace-nowrap text-[color:var(--color-foreground)] [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
  cell: "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
  caption: "mt-4 text-sm text-[color:var(--color-muted-foreground)]",
} as const

export const popoverClassName =
  "theme-menu-surface z-[var(--z-overlay)] w-72 rounded-[var(--radius-md)] border p-4 text-[color:var(--color-popover-foreground)] outline-none"

export const tooltipClassName =
  "z-50 overflow-hidden rounded-[var(--radius-sm)] border border-[color:var(--color-border)] bg-[color:var(--color-popover)] px-3 py-1.5 text-xs leading-[1.4] text-[color:var(--color-popover-foreground)] shadow-[var(--shadow-md)]"

export const hintTooltipClassNames = {
  trigger: "inline-flex cursor-help",
  content: "max-w-80 text-[0.75rem] leading-[1.55]",
} as const

export const dialogClassNames = {
  overlay:
    "fixed inset-0 z-[var(--z-dialog)] bg-slate-950/50 transition-opacity duration-200",
  viewport: "fixed inset-0 z-[var(--z-dialog)]",
  content: [
    "theme-surface-glass fixed left-1/2 top-1/2 z-[var(--z-dialog)] flex w-[min(calc(100%-2rem),34rem)] max-h-[calc(100vh-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-auto rounded-[var(--radius-lg)] border p-6 outline-none",
    "focus-visible:outline-none",
  ].join(" "),
  contentUnstyled: "fixed z-[var(--z-dialog)]",
  closeButton: [
    "absolute right-4 top-4 inline-flex items-center justify-center rounded-[var(--radius-xs)] bg-transparent text-[color:var(--color-muted-foreground)] opacity-80 transition-colors duration-150",
    "hover:bg-[color:var(--button-ghost-hover)] hover:text-[color:var(--color-foreground)] hover:opacity-100",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-ring)]",
    "disabled:pointer-events-none disabled:opacity-45",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  header: "flex flex-col gap-2 text-center sm:text-left",
  footer: "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
  title: "text-lg font-semibold leading-[1.15] text-[color:var(--color-foreground)]",
  description: "text-sm text-[color:var(--color-muted-foreground)]",
} as const

export const dropdownMenuClassNames = {
  content:
    "theme-menu-surface z-[var(--z-overlay)] min-w-32 overflow-x-hidden overflow-y-auto rounded-[var(--radius-md)] border p-1 text-[color:var(--color-popover-foreground)]",
  item: [
    "relative flex w-full items-center gap-2 rounded-[var(--radius-sm)] bg-transparent px-[0.55rem] py-[0.4rem] text-left text-sm text-inherit outline-none transition-colors duration-150",
    "cursor-default select-none",
    "hover:bg-[color:var(--button-ghost-hover)] focus-visible:bg-[color:var(--button-ghost-hover)] data-[highlighted]:bg-[color:var(--button-ghost-hover)]",
    "data-[variant=destructive]:text-[color:var(--color-error-foreground)] data-[variant=destructive]:hover:bg-[color:var(--color-error-soft)] data-[variant=destructive]:focus-visible:bg-[color:var(--color-error-soft)] data-[variant=destructive]:data-[highlighted]:bg-[color:var(--color-error-soft)]",
    "disabled:pointer-events-none disabled:opacity-50",
    "data-[inset]:pl-8",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-[color:var(--color-muted-foreground)]",
  ].join(" "),
  itemWithIndicator: "pl-8",
  indicator:
    "pointer-events-none absolute left-[0.55rem] top-1/2 inline-flex h-[0.9rem] w-[0.9rem] -translate-y-1/2 items-center justify-center [&_svg]:text-current",
  label:
    "px-[0.55rem] py-[0.4rem] text-sm font-semibold text-[color:var(--color-foreground)] data-[inset]:pl-8",
  separator:
    "theme-menu-separator -mx-1 my-1 h-px bg-[color:var(--color-border)]",
  shortcut:
    "ml-auto text-xs uppercase tracking-[0.16em] text-[color:var(--color-muted-foreground)]",
  subChevron: "ml-auto",
  subContent:
    "theme-menu-surface z-[var(--z-overlay)] min-w-32 overflow-hidden rounded-[var(--radius-md)] border p-1 text-[color:var(--color-popover-foreground)]",
} as const
